/**
 * Cloudflare Turnstile client helpers for Hosted Auth Portal (D-0065).
 * Sitekey comes from portal context or VITE_TURNSTILE_SITE_KEY — never the secret.
 */

export type TurnstilePublicInfo = {
  enabled: boolean;
  site_key?: string;
  mode?: string;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: (errorCode?: string) => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          action?: string;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
    };
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = "cf-turnstile-script";
// Explicit render; onload callback name is set before inject (and polled as fallback).
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";

let scriptPromise: Promise<void> | null = null;

/** Resolve sitekey from portal context, falling back to Vite env for /v1 forms. */
export function resolveTurnstileSiteKey(
  info?: TurnstilePublicInfo | null,
): string {
  const fromInfo = info?.site_key?.trim();
  if (fromInfo) {
    return fromInfo;
  }
  const fromEnv = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)
    ?.trim();
  return fromEnv ?? "";
}

export function isTurnstileEnabled(info?: TurnstilePublicInfo | null): boolean {
  // Server can explicitly disable even if a build-time sitekey exists.
  if (info && typeof info.enabled === "boolean" && !info.enabled) {
    return false;
  }
  // enabled:true (or missing info for env-only forms) needs a sitekey to render.
  return Boolean(resolveTurnstileSiteKey(info));
}

/**
 * Load Turnstile script once (explicit render).
 * Handles cached scripts where onload may fire before our handler is attached.
 */
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      if (!window.turnstile) return;
      settled = true;
      resolve();
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      scriptPromise = null;
      reject(new Error(message));
    };

    // Prefer official onload; also poll in case the cached script already ran.
    const previous = window.onTurnstileLoad;
    window.onTurnstileLoad = () => {
      try {
        previous?.();
      } catch {
        /* ignore */
      }
      finish();
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!existing) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () =>
        fail(
          "Failed to load Cloudflare Turnstile (blocked by network, CSP, or ad-blocker)",
        );
      script.onload = () => {
        // Some environments fire load after api.js defines turnstile without onload.
        finish();
      };
      document.head.appendChild(script);
    } else {
      existing.addEventListener("load", () => finish());
      existing.addEventListener("error", () =>
        fail("Failed to load Cloudflare Turnstile"),
      );
    }

    // Poll: handles StrictMode remount + script already fully loaded before handlers.
    let ticks = 0;
    const timer = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(timer);
        finish();
        return;
      }
      ticks += 1;
      if (ticks > 100) {
        // ~10s
        window.clearInterval(timer);
        fail("Cloudflare Turnstile script timed out");
      }
    }, 100);
  });

  return scriptPromise;
}

/** Reset module state (tests only). */
export function resetTurnstileScriptStateForTests() {
  scriptPromise = null;
}
